/**
 * Enable Banking — the bank-feed sync hooks.
 *
 * Every call goes through the Hono backend at `/api/v1/enable-banking`
 * (docs/BACKEND_API.md): the two tables are `SECURITY INVOKER` with org RLS for
 * reads and RESTRICTIVE `verify_backend_token()` policies for writes, so the
 * browser has no direct path to either. Shapes are the wire contract in
 * `@releve/shared` (`packages/shared/src/schemas/enableBanking.ts`) — never
 * re-declared here.
 *
 * The routes are mounted OUTSIDE the typed `routes` chain (apps/api/src/index.ts
 * :150-158, to keep Hono's RPC inference under its depth limit), so these use the
 * untyped `authedGet`/`authedPost` helpers rather than the `api` client — the
 * same reason `useClearedBalances` does.
 *
 * ---------------------------------------------------------------------------
 * QUERY KEYS — load-bearing, and asserted by useBankConnections.test.ts.
 *
 *   ['bankConnections', orgId]                       the connections + accounts
 *   ['bankConnections', orgId, 'aspsps', country]     the country's bank list
 *
 * The aspsp key nests UNDER the connections key on purpose (the plan specifies
 * both): a country's bank list changes monthly at most, so it carries a 30-minute
 * staleTime and the occasional extra refetch from a mutation's prefix
 * invalidation costs nothing — Dialog A is conditionally mounted, so the query is
 * usually inactive when a mutation fires.
 *
 * EVERY mutation invalidates `['bankConnections', orgId]`. The SYNC mutation
 * additionally mirrors `useCAMTImport.ts:570-579` exactly, because it is the same
 * event — new feed rows, nothing posted to the ledger:
 *
 *   ['accountingImports', orgId]                      the feed grid's liveness
 *   ['accountBalances', orgId, 'clearingSummary']      the clearing bridge
 *   ['import-batches', orgId, bankAccountId]           Import History
 *
 * The middle one is NOT `['clearedBalances', orgId]`. `useClearedBalances.ts:33-36`
 * names that exact string as the anti-pattern: a standalone key would be
 * invalidated by nothing and go permanently stale after the first ledger write.
 * The nested key lives "for free" under the 26 existing `['accountBalances']`
 * posting invalidations, and the three bridge-moving-without-posting mutations
 * (`useClearingActions` skip, `useCAMTImport`, `useRemoveImportBatch`) invalidate
 * it explicitly — this is now the fourth.
 * ---------------------------------------------------------------------------
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import type {
  BankConnectionAccountWire,
  BankConnectionCallbackResponse,
  BankConnectionWire,
  DisconnectBankConnectionResponse,
  LinkBankConnectionAccountResponse,
  ListAspspsResponse,
  ListBankConnectionsResponse,
  StartBankConnectionResponse,
  SyncBankConnectionAccountResponse,
} from '@releve/shared';
import { authedGet, authedPost } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';
import { addBreadcrumb } from '@/lib/sentry';
import { trackEvent } from '@/lib/posthog';

/** The org feature flag both halves of this feature gate on. */
export const BANK_FEED_SYNC_FEATURE = 'bank_feed_sync';

const BASE = '/api/v1/enable-banking';

/**
 * Synthetic envelope code for "the bank consent is gone — reconnect".
 *
 * `App.tsx:228-245` captures EVERY mutation error to Sentry unless the mutation
 * declares the failure expected via `meta.expectedErrorCodes`, which it reads
 * from `error.envelope.code`. A lapsed PSD2 consent is an ORDINARY state with a
 * Reconnect button (there is no unattended refresh), so shipping one as a Sentry
 * error would bury real failures under consent lapses.
 *
 * The backend's error envelope is `{ error: message }` — `app.onError`
 * (apps/api/src/index.ts:246-266) carries no code field — so the code cannot come
 * off the wire today. What CAN: `enableBankingErrorStatus` maps every
 * session-state failure (`EXPIRED_SESSION`, `REVOKED_SESSION`, `CLOSED_SESSION`,
 * `WRONG_SESSION_STATUS`, `SESSION_DOES_NOT_EXIST`, `ALREADY_AUTHORIZED`) and
 * only those to **409**, and deliberately never returns 401 so a lapsed BANK
 * consent cannot be mistaken for a lapsed login. So a 409 from these routes is
 * exactly the reconnect state, and this tags it as such client-side.
 */
export const BANK_CONSENT_ERROR_CODE = 'bank_consent_expired';

/** Declared on every mutation below via `meta.expectedErrorCodes`. */
export const EXPECTED_BANK_CONNECTION_ERROR_CODES = [BANK_CONSENT_ERROR_CODE] as const;

/**
 * True when a failure is the routine "reconnect your bank" state rather than an
 * incident. Read structurally (`ApiError.status`) so the check survives a mocked
 * `@/lib/api` in tests and a non-`ApiError` rejection alike.
 */
export function isBankConsentError(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 409;
}

/**
 * Stamp a 409 with {@link BANK_CONSENT_ERROR_CODE} so the global
 * `MutationCache.onError` skips it. Mutates the thrown error in place and
 * rethrows — the message, status and `Retry-After` all stay intact for the local
 * `onError` toast.
 */
async function callWithConsentCode<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (error) {
    if (isBankConsentError(error)) {
      (error as { envelope?: { code?: string } }).envelope = {
        code: BANK_CONSENT_ERROR_CODE,
      };
    }
    throw error;
  }
}

/** Shared mutation options: expected-code declaration + the Sentry-tagged onError. */
function bankConnectionMutationMeta() {
  return { expectedErrorCodes: EXPECTED_BANK_CONNECTION_ERROR_CODES } as const;
}

/**
 * `Sentry.withScope` + tags, per `useAddExpenseFromImport.ts:195-203`: a bare
 * `captureException` here would only DOUBLE the event the global
 * `MutationCache.onError` already files. The scope is what adds value, and it
 * must be captured inside the callback because the global handler runs a
 * microtask later, after the scope is popped.
 *
 * Consent lapses are not captured at all — they are declared expected above.
 * No `console.error` alongside the capture (docs/OBSERVABILITY.md).
 */
function captureBankConnectionError(
  error: Error,
  action: string,
  extras: Record<string, string | undefined>,
): void {
  if (isBankConsentError(error)) return;
  Sentry.withScope((scope) => {
    scope.setTag('feature', 'bank-feed');
    scope.setTag('bank_connection_action', action);
    for (const [key, value] of Object.entries(extras)) {
      if (value) scope.setExtra(key, value);
    }
    Sentry.captureException(error);
  });
}

const asError = (err: unknown): Error =>
  err instanceof Error ? err : new Error(String(err));

/* ------------------------------------------------------------------------- *
 * Reads
 * ------------------------------------------------------------------------- */

/**
 * The org's bank connections, each with its accounts.
 *
 * Hoisted to the tab rather than queried inside the status popover, so the
 * toolbar button can read its own state (Connect / Sync / Reconnect) without the
 * popover firing a query while closed.
 *
 * `staleTime` 5 min: connection state only moves when a mutation here moves it,
 * and every one of them invalidates this key.
 */
export const useBankConnections = (organizationId?: string) => {
  const { enabled: featureEnabled } = useFeatureEnabled(BANK_FEED_SYNC_FEATURE);

  return useQuery({
    queryKey: ['bankConnections', organizationId],
    queryFn: async (): Promise<BankConnectionWire[]> => {
      if (!organizationId) return [];
      const result = await authedGet<ListBankConnectionsResponse>(
        `${BASE}/orgs/${organizationId}/connections`,
      );
      return result.connections;
    },
    // Gated client-side as well as at the sub-router: without this every org
    // would fire a request the backend answers 403, and App.tsx:223 correctly
    // does not retry 403s — it would just be a dead query on every mount.
    enabled: !!organizationId && featureEnabled,
    staleTime: 5 * 60_000,
  });
};

/**
 * The banks available in one country, for Dialog A's picker.
 *
 * `country` is required rather than defaulted from `organizations.country` —
 * that column is nullable and unset for most orgs, and an NL org connecting a
 * Belgian account is an ordinary case.
 *
 * `staleTime` 30 min (docs/DATA_LAYER.md:693-718): a country's bank list changes
 * monthly at most.
 */
export const useAspsps = (organizationId?: string, country?: string) => {
  const { enabled: featureEnabled } = useFeatureEnabled(BANK_FEED_SYNC_FEATURE);

  return useQuery({
    queryKey: ['bankConnections', organizationId, 'aspsps', country],
    queryFn: async () => {
      if (!organizationId || !country) return [];
      const result = await authedGet<ListAspspsResponse>(
        `${BASE}/orgs/${organizationId}/aspsps?country=${encodeURIComponent(country)}`,
      );
      return result.aspsps;
    },
    enabled: !!organizationId && !!country && featureEnabled,
    staleTime: 30 * 60_000,
  });
};

/**
 * The connection account linked to one ledger account, or null.
 *
 * A pure selector over an already-cached `useBankConnections` result, deliberately
 * NOT a second query: two live queries holding the same rows would refetch on
 * independent timers and could disagree (the reasoning in `useClearedBalances`).
 */
export function findConnectionAccount(
  connections: BankConnectionWire[] | undefined,
  chartOfAccountsId: string | undefined,
): { connection: BankConnectionWire; account: BankConnectionAccountWire } | null {
  if (!connections || !chartOfAccountsId) return null;
  for (const connection of connections) {
    const account = connection.accounts.find(
      (row) => row.chart_of_accounts_id === chartOfAccountsId,
    );
    if (account) return { connection, account };
  }
  return null;
}

/* ------------------------------------------------------------------------- *
 * Mutations
 * ------------------------------------------------------------------------- */

interface StartBankConnectionArgs {
  aspsp_name: string;
  aspsp_country: string;
  psu_type: 'business' | 'personal';
  /** Org-relative path (query string included) the callback returns the user to. */
  return_to: string;
  /** Present ⇒ RECONNECT of an existing connection rather than a new one. */
  connection_id?: string | null;
}

/**
 * Start (or re-start) an authorization. The caller performs the full-page
 * redirect to `url` — the SPA unloads there, which is precisely why connect and
 * link are two independent dialogs and not a wizard.
 */
export const useStartBankConnection = (organizationId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: ['bankConnections', 'authorize'],
    meta: bankConnectionMutationMeta(),
    mutationFn: async (args: StartBankConnectionArgs) => {
      if (!organizationId) throw new Error('Missing organization');
      addBreadcrumb('bankConnections.authorize: started', {
        aspsp: `${args.aspsp_country}/${args.aspsp_name}`,
        reconnect: Boolean(args.connection_id),
      });
      return callWithConsentCode(() =>
        authedPost<StartBankConnectionResponse>(
          `${BASE}/orgs/${organizationId}/connections/authorize`,
          args,
        ),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['bankConnections', organizationId],
      });
    },
    onError: (err) => {
      const error = asError(err);
      captureBankConnectionError(error, 'authorize', { organizationId });
      toast.error(error.message || t('bankAccounts.sync.toast.connectFailed'));
    },
  });
};

interface CompleteBankConnectionArgs {
  /**
   * Taken from the signed `state`, not from context: `/bank-callback` is org-less
   * (it renders outside `OrgLayout`), so the org id has to travel in the payload.
   */
  organizationId: string;
  code: string;
  state: string;
}

/**
 * Complete an authorization from `/bank-callback`.
 *
 * No toast: the callback page IS the feedback, and it navigates on to
 * `return_to`. No client-side feature gate either — the page is org-less, so
 * `useFeatureEnabled` (which reads the selected organization) cannot answer
 * there; the sub-router's 403 is the gate.
 */
export const useCompleteBankConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['bankConnections', 'callback'],
    meta: bankConnectionMutationMeta(),
    mutationFn: async ({ organizationId, code, state }: CompleteBankConnectionArgs) => {
      addBreadcrumb('bankConnections.callback: started', { organizationId });
      return callWithConsentCode(() =>
        authedPost<BankConnectionCallbackResponse>(
          `${BASE}/orgs/${organizationId}/connections/callback`,
          { code, state },
        ),
      );
    },
    onSuccess: (_result, { organizationId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['bankConnections', organizationId],
      });
    },
    onError: (err, { organizationId }) => {
      captureBankConnectionError(asError(err), 'callback', { organizationId });
    },
  });
};

/**
 * Revoke a connection. The local row is marked revoked either way; when Enable
 * Banking's own session delete failed the response says so, and the toast passes
 * that on — on a bank permitting one concurrent session per user a stale remote
 * session blocks the next authorization.
 */
export const useDisconnectBankConnection = (organizationId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: ['bankConnections', 'disconnect'],
    meta: bankConnectionMutationMeta(),
    mutationFn: async ({ connectionId }: { connectionId: string }) => {
      if (!organizationId) throw new Error('Missing organization');
      addBreadcrumb('bankConnections.disconnect: started', { connectionId });
      return callWithConsentCode(() =>
        authedPost<DisconnectBankConnectionResponse>(
          `${BASE}/orgs/${organizationId}/connections/${connectionId}/disconnect`,
          {},
        ),
      );
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: ['bankConnections', organizationId],
      });
      if (result.remote_session_deleted) {
        toast.success(t('bankAccounts.sync.toast.disconnected'));
      } else {
        toast.warning(t('bankAccounts.sync.toast.disconnectedRemoteFailed'));
      }
    },
    onError: (err, { connectionId }) => {
      const error = asError(err);
      captureBankConnectionError(error, 'disconnect', { organizationId, connectionId });
      toast.error(error.message || t('bankAccounts.sync.toast.disconnectFailed'));
    },
  });
};

interface LinkBankConnectionAccountArgs {
  /** `bank_connection_accounts.id` — our own row id, never the EB uid. */
  accountId: string;
  /** Null UNLINKS. Imported rows are left intact (the UI owns the re-import warning). */
  chartOfAccountsId: string | null;
  /** Only meaningful for a credit-card target; ignored on unlink. */
  flipCardSign?: boolean;
  /**
   * Suppress the SUCCESS toast only. A caller that links several accounts in
   * one pass (the set-up-accounts flow) owns a single summary toast; N per-row
   * successes would bury it. The FAILURE toast, the Sentry capture and the
   * breadcrumbs stay unconditional — a silent failure is never what the caller
   * meant, and the batch keeps going after a bad row.
   */
  silent?: boolean;
}

/**
 * Link a bank account to a ledger account, or unlink it with a null id.
 *
 * The guards (feed account type, currency match with `XXX` treated as "matches
 * anything", neither side already linked) live in the route and in a partial
 * unique index; their messages arrive here as the error text.
 */
export const useLinkBankConnectionAccount = (organizationId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: ['bankConnections', 'link'],
    meta: bankConnectionMutationMeta(),
    mutationFn: async ({
      accountId,
      chartOfAccountsId,
      flipCardSign,
    }: LinkBankConnectionAccountArgs) => {
      if (!organizationId) throw new Error('Missing organization');
      addBreadcrumb('bankConnections.link: started', {
        accountId,
        unlink: chartOfAccountsId === null,
      });
      return callWithConsentCode(() =>
        authedPost<LinkBankConnectionAccountResponse>(
          `${BASE}/orgs/${organizationId}/connection-accounts/${accountId}/link`,
          {
            chart_of_accounts_id: chartOfAccountsId,
            ...(flipCardSign === undefined ? {} : { flip_card_sign: flipCardSign }),
          },
        ),
      );
    },
    onSuccess: (_result, { chartOfAccountsId, silent }) => {
      void queryClient.invalidateQueries({
        queryKey: ['bankConnections', organizationId],
      });
      if (silent) return;
      toast.success(
        chartOfAccountsId === null
          ? t('bankAccounts.sync.toast.unlinked')
          : t('bankAccounts.sync.toast.linked'),
      );
    },
    onError: (err, { accountId }) => {
      const error = asError(err);
      captureBankConnectionError(error, 'link', { organizationId, accountId });
      toast.error(error.message || t('bankAccounts.sync.toast.linkFailed'));
    },
  });
};

interface SyncBankConnectionAccountArgs {
  /** `bank_connection_accounts.id`. */
  accountId: string;
  /**
   * The LEDGER account (`chart_of_accounts.id`) this feed lands in. Required, not
   * derived from the response, because it is the third segment of the
   * `['import-batches', orgId, bankAccountId]` key the caller's Import History
   * panel is already keyed by — deriving it would let the two drift.
   */
  bankAccountId: string;
}

/**
 * One Sync click: fetch everything the bank offers (first sync) or what is new
 * since the last one, stage it into `accounting_imports`
 * under ONE `import_batch_id`, and cache the bank-reported balance.
 *
 * Partial success is normal and is REPORTED, never hidden — chunks already
 * written persist and a retry dedups on `entry_reference`, so the three FEED
 * invalidations live in `onSettled` and fire on every outcome, thrown error
 * included. Only the connection's own key is success-scoped.
 */
export const useSyncBankConnectionAccount = (organizationId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: ['bankConnections', 'sync'],
    meta: bankConnectionMutationMeta(),
    mutationFn: async ({ accountId }: SyncBankConnectionAccountArgs) => {
      if (!organizationId) throw new Error('Missing organization');
      addBreadcrumb('bankConnections.sync: started', { accountId });
      return callWithConsentCode(() =>
        authedPost<SyncBankConnectionAccountResponse>(
          `${BASE}/orgs/${organizationId}/connection-accounts/${accountId}/sync`,
          {},
        ),
      );
    },
    onSuccess: (result) => {
      trackEvent('bank_import_completed', {
        source: 'enablebanking',
        inserted: result.inserted_count,
        skipped: result.duplicate_count + result.loose_duplicate_count + result.skipped_count,
        truncated: result.truncated,
      });
      addBreadcrumb('bankConnections.sync: done', {
        inserted: result.inserted_count,
        duplicates: result.duplicate_count,
        loose_duplicates: result.loose_duplicate_count,
        loose_duplicates_suggested: result.loose_duplicate_suggested_count,
        skipped: result.skipped_count,
      });

      // last_synced_at, the balance cache and the connection's last_error all
      // live on this key.
      void queryClient.invalidateQueries({
        queryKey: ['bankConnections', organizationId],
      });

      // Appended to whichever outcome toast fires rather than threaded through
      // each key's interpolation — the same shape as useCAMTImport's
      // `leftOutMsg`.
      const duplicatesMsg =
        result.duplicate_count > 0
          ? ` ${t('bankAccounts.sync.toast.duplicatesSkipped', { count: result.duplicate_count })}`
          : '';
      // Cross-source twins: synced lines that copy a CAMT/CSV line already in
      // the feed and landed pre-skipped as duplicates. A separate sentence from
      // `duplicatesMsg` because the user's follow-up differs — an exact
      // duplicate needs nothing, a loose one is a judgement call they may want
      // to Undo in the grid. NOT in `inserted_count` (the API subtracts them),
      // so the headline number and this note never double-count.
      const crossSourceMsg =
        result.loose_duplicate_count > 0
          ? ` ${t('bankAccounts.sync.toast.crossSourceDuplicates', { count: result.loose_duplicate_count })}`
          : '';
      // Weak twins (`remittance_text` / `key_only`) the sync would not act on
      // alone: inserted PENDING — they are part of `inserted_count`, so this
      // is a note on the headline number, not a subtraction from it — with a
      // "Duplicate of …" chip waiting in the grid. Right after the strong
      // ones, so the two duplicate sentences read as one thought: "these were
      // skipped, those are for you to decide". Absent from a response that
      // predates the field (`?? 0`).
      const suggestedMsg =
        (result.loose_duplicate_suggested_count ?? 0) > 0
          ? ` ${t('bankAccounts.sync.toast.crossSourcePossibleDuplicates', { count: result.loose_duplicate_suggested_count })}`
          : '';
      // A balances failure never fails the sync (the rows are the valuable
      // part), so it is a note on the outcome, not an outcome of its own.
      const balanceMsg = result.balance_error
        ? ` ${t('bankAccounts.sync.toast.balanceUnavailable')}`
        : '';
      // The loose check hit the API's candidate row cap on at least one chunk,
      // so it may have UNDER-flagged — a twin it never saw is `pending` beside
      // its statement copy. Never a wrong flag, so it is the last note, not a
      // warning outcome: the user is asked to look, not told something broke.
      const cappedMsg = result.loose_duplicate_capped
        ? ` ${t('bankAccounts.sync.toast.crossSourceCheckCapped')}`
        : '';
      // Order is fixed: counts first (exact, then loose-skipped, then
      // loose-suggested), then the two notes (balance, then the cap). Tests
      // pin it.
      const suffix = `${duplicatesMsg}${crossSourceMsg}${suggestedMsg}${balanceMsg}${cappedMsg}`;

      // Truncation first, and it is NOT the partial-failure arm: nothing failed,
      // the statement is simply longer than one click fetches. The rows that
      // landed are kept, `last_synced_at` did not move and the connection carries
      // no error — so this is a warning that says "sync again to continue",
      // never a success ("all caught up") and never a failure.
      if (result.truncated) {
        toast.warning(
          t('bankAccounts.sync.toast.syncTruncated', {
            success: result.inserted_count,
            skipped: result.skipped_count,
          }) + suffix,
        );
        return;
      }

      // Three-way, per useCAMTImport.ts:588-605. `skipped_count` is the mapper's
      // refusals; `duplicate_count` is the RPC's dedup verdict and is the steady
      // state of a re-sync, so it never makes the outcome look degraded.
      if (!result.transactions_error && result.skipped_count === 0) {
        toast.success(
          t('bankAccounts.sync.toast.syncSuccess', { count: result.inserted_count }) + suffix,
        );
      } else if (!result.transactions_error) {
        toast.success(
          t('bankAccounts.sync.toast.syncSuccessWithSkipped', {
            success: result.inserted_count,
            skipped: result.skipped_count,
          }) + suffix,
        );
      } else {
        toast.warning(
          t('bankAccounts.sync.toast.syncPartial', {
            success: result.inserted_count,
            skipped: result.skipped_count,
            error: translateEnableBankingError(t, result.transactions_error),
          }) + suffix,
        );
      }
    },
    onError: (err, { accountId }) => {
      const error = asError(err);
      captureBankConnectionError(error, 'sync', { organizationId, accountId });
      toast.error(
        isBankConsentError(error)
          ? t('bankAccounts.sync.toast.reconnectRequired')
          : error.message || t('bankAccounts.sync.toast.syncFailed'),
      );
    },
    // The feed keys live HERE, not in onSuccess: the route flushes chunks to
    // `accounting_imports` as they fill, so a rejection — a timeout, a 502, the
    // platform killing the request — can still leave rows on disk. `withTimeout`
    // abandons the response, it does not cancel the write. Invalidating on both
    // outcomes is safe (a no-op refetch at worst) and is the only way a failed
    // run's landed rows reach the grid without a manual reload.
    onSettled: (_result, _error, { bankAccountId }) => {
      // --- the useCAMTImport.ts:570-579 set, exactly ---
      // Prefix match: the feed grid is keyed ['accountingImports', orgId,
      // bankAccountId, …].
      void queryClient.invalidateQueries({
        queryKey: ['accountingImports', organizationId],
      });
      // New feed rows move the clearing bridge (bank balance + awaiting posting)
      // without posting anything, so the ['accountBalances'] invalidations the
      // posting paths fire never reach it. NOT ['clearedBalances', orgId] —
      // see useClearedBalances.ts:33-36.
      void queryClient.invalidateQueries({
        queryKey: ['accountBalances', organizationId, 'clearingSummary'],
      });
      // kebab-case and outside every prefix above; without it Import History
      // never shows the batch this sync just created.
      void queryClient.invalidateQueries({
        queryKey: ['import-batches', organizationId, bankAccountId],
      });
    },
  });
};

/**
 * An Enable Banking TEXT error code, translated.
 *
 * Only the sync response carries a code (`transactions_error` / `balance_error`
 * come back inside a 200 body); an HTTP failure arrives as untranslated English
 * from the API, which the docs say is for logs and support, never for a toast on
 * its own. `defaultValue` keeps a code we have no key for from rendering as a
 * raw i18n path.
 *
 * The table is keyed by the code, so every arm is a leaf under
 * `bankAccounts.sync.errors.*` — including the codes the API raises ITSELF
 * rather than relaying from the bank (`EbErrorCode` in `@releve/shared` is the
 * widened union). One matters here: `MIGRATION_REQUIRED`, thrown when the sync
 * flagged cross-source duplicates and its capability probe (one empty
 * `bulk_insert_accounting_imports` call, made before the first flagged insert)
 * showed the deployed RPC predates the `skip_reason` migration. It is a
 * deploy-order fault, not a bank problem — the probe refuses BEFORE the chunk is
 * written, so nothing landed and the rows are re-fetched by the next sync — so
 * its leaf tells the operator to apply the migration, and must NOT read like
 * the consent/reconnect family. A `common.*` leaf is not reachable
 * from another namespace (no `fallbackNS`), which is why the string lives here
 * beside the bank's codes and nowhere else.
 */
export function translateEnableBankingError(
  t: (key: string, options?: Record<string, unknown>) => string,
  error: { code: string; message: string },
): string {
  return t(`bankAccounts.sync.errors.${error.code}`, { defaultValue: error.message });
}
